import * as React from "react";
import * as yup from "yup";
import { parseISO } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    TextArea,
} from "@jhool-io/fe-components";
import {
    ISessionNote,
    IEditSupervisorDiagnosticReview,
    IGetClientNotes,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";

import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditSupervisorDiagnosticReviewProps {
    noteDetails: ISessionNote;
}

export default function EditSupervisorDiagnosticReview({
    noteDetails,
}: EditSupervisorDiagnosticReviewProps) {
    const approvalStatement =
        "I have reviewed the clinical information and approve the diagnostic change noted above.";

    const [localDateOfReview, setLocalDateOfReview] =
        React.useState<Date | null>(
            noteDetails?.note_content?.date_of_review
                ? parseISO(noteDetails?.note_content?.date_of_review)
                : null
        );
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const isSigning = React.useRef(false);
    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        watch,
        setValue,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditSupervisorDiagnosticReview>({
        resolver: yupResolver(
            yup.object({
                date_of_review: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Date of review is required")
                        .typeError("Date of review is required"),
                    otherwise: yup.date().nullable(),
                }),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                }),
                revised_diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                }),
                new_clinical_information_supporting_diagnostic_change: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .required(
                                "New clinical information supporting diagnostic change is required"
                            ),
                        otherwise: yup.string(),
                    }),
                diagnostic_rationale: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Diagnostic rationale is required"),
                    otherwise: yup.string(),
                }),
                reviewed_and_approved_by_lp: yup
                    .array()
                    .of(yup.string())
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Supervisory determination is required")
                            .required("Supervisory determination is required"),
                        otherwise: yup.array().nullable(),
                    }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content || undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("date_of_review", note.note_content.date_of_review);
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue(
            "revised_diagnosis_codes",
            note.note_content.revised_diagnosis_codes
        );
        setValue(
            "new_clinical_information_supporting_diagnostic_change",
            note.note_content
                .new_clinical_information_supporting_diagnostic_change
        );
        setValue(
            "diagnostic_rationale",
            note.note_content.diagnostic_rationale
        );
        setValue(
            "reviewed_and_approved_by_lp",
            note.note_content.reviewed_and_approved_by_lp
        );
        setValue("signature", note.note_content.signature);
        setShowPrompt(false);
    };

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes?.data?.map((note) => (
                                <DropdownItem
                                    key={note.note_id}
                                    onClick={() => {
                                        setShowPrompt(true);
                                        setSelectedNote(note);
                                    }}
                                >
                                    <div>
                                        {makeStringFirstLetterCapital(
                                            removeEnumUnderscore(note.note_type)
                                        )}{" "}
                                        - Note content Details -{" "}
                                        {formatDate(note.date_of_service)}
                                    </div>
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="edit supervisor diagnostic review"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>

                <section className="mb-10">
                    <div className="fg">
                        <Controller
                            name="date_of_review"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label="Date of Review"
                                    onChange={(date) => {
                                        field.onChange(date);
                                        setLocalDateOfReview(date);
                                    }}
                                    selected={localDateOfReview}
                                    value={
                                        field.value
                                            ? formatDate(field.value, true)
                                            : undefined
                                    }
                                    hasError={!!errors.date_of_review}
                                    errorText={
                                        errors.date_of_review?.type ===
                                        "typeError"
                                            ? "Date of review is required"
                                            : errors.date_of_review?.message
                                    }
                                    shouldCloseOnSelect
                                    isRequired
                                />
                            )}
                        />
                    </div>

                    <div className="fg">
                        <Controller
                            name="diagnosis_codes"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label="Current diagnosis (prior to change)"
                                    value={field.value}
                                    onChange={field.onChange}
                                    isRequired
                                    errorText={
                                        errors.diagnosis_codes?.type ===
                                        "typeError"
                                            ? "Diagnosis is required"
                                            : errors.diagnosis_codes?.message
                                    }
                                    hideSelectedOptions
                                    isMulti
                                />
                            )}
                        />
                    </div>

                    <div className="fg">
                        <Controller
                            name="revised_diagnosis_codes"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label="Revised diagnosis (post-review)"
                                    value={field.value}
                                    onChange={field.onChange}
                                    isRequired
                                    errorText={
                                        errors.revised_diagnosis_codes?.message
                                    }
                                    hideSelectedOptions
                                    isMulti
                                />
                            )}
                        />
                    </div>

                    <div className="fg ">
                        <TextArea
                            {...register(
                                "new_clinical_information_supporting_diagnostic_change"
                            )}
                            label="New Clinical Information Supporting Diagnostic Change"
                            placeholder="Briefly summarize new or clarified clinical information obtained through treatment that was not fully available at intake."
                            defaultValue={
                                noteDetails.note_content
                                    ?.new_clinical_information_supporting_diagnostic_change
                            }
                            hasError={
                                !!errors.new_clinical_information_supporting_diagnostic_change
                            }
                            errorText={
                                errors
                                    .new_clinical_information_supporting_diagnostic_change
                                    ?.message
                            }
                            isRequired
                        />
                    </div>

                    <div className="fg">
                        <TextArea
                            {...register("diagnostic_rationale")}
                            label="Diagnostic Rationale"
                            placeholder="Explain why the revised diagnosis better reflects the client’s clinical presentation, including relevant diagnostic criteria or specifiers."
                            defaultValue={
                                noteDetails.note_content?.diagnostic_rationale
                            }
                            hasError={!!errors.diagnostic_rationale}
                            errorText={errors.diagnostic_rationale?.message}
                            isRequired
                        />
                    </div>

                    <h3 className="note-label note-label-req">
                        Supervisory determination
                    </h3>
                    <div className="note-checkboxes">
                        <Controller
                            name="reviewed_and_approved_by_lp"
                            control={control}
                            render={({ field }) => (
                                <FormCheckbox
                                    label={approvalStatement}
                                    value={approvalStatement}
                                    checked={
                                        field.value?.includes(
                                            approvalStatement
                                        ) ?? false
                                    }
                                    onChange={(e) =>
                                        field.onChange(
                                            e.target.checked
                                                ? [approvalStatement]
                                                : null
                                        )
                                    }
                                />
                            )}
                        />
                    </div>
                    {errors.reviewed_and_approved_by_lp ? (
                        <div className="note-error">
                            <p>{errors.reviewed_and_approved_by_lp?.message}</p>
                        </div>
                    ) : null}
                </section>

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>

                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
